import { ReactNode } from 'react';
import { Typography } from 'beek-design-system';
import NextLink from './NextLink';
import styled from 'styled-components';

export type LinkProps = {
  href: string;
  children: string | ReactNode | ReactNode[];
  external?: boolean;
  prefetch?: boolean;
  replaceHistory?: boolean;
  colorVariant?: string;
  typographyType?: string;
  textDecoration?: string;
  activeTextDecoration?: string;
  hoverTextDecoration?: string;
  hoverTextColor?: string;
  hoverDecorationColor?: string;
  activeTextColor?: string;
  activeDecorationColor?: string;
  display?: string;
  marginTop?: string;
  marginBottom?: string;
  marginRight?: string;
  marginLeft?: string;
  textColorVariant?: string;
  type?: string;
  hoverType?: string;
  pointerEvent?: string;
  avoidNextLink?: boolean;
  title?: string;
  locale?: string | false | undefined;
  tabIndex?: number;
};
type AnchorContainerProps = {
  display?: string;
  marginTop?: string;
  marginBottom?: string;
  marginRight?: string;
  marginLeft?: string;
  colorVariant?: string;
  textDecoration?: string;
  activeTextDecoration?: string;
  hoverTextDecoration?: string;
  hoverDecorationColor?: string;
  activeDecorationColor?: string;
  hoverTextColor?: string;
  activeTextColor?: string;
  type?: string;
  hoverType?: string;
  pointerEvent?: string;
};

interface IDictionary {
  [index: string]: string;
}

const defaultProps = {
  external: false,
  prefetch: true,
  replaceHistory: false,
  textDecoration: 'underline',
  activeTextDecoration: 'underline',
  hoverTextDecoration: 'underline',
  colorVariant: 'gray100',
  typographyType: 'body1',
  display: 'block',
  marginTop: '0rem',
  marginBottom: '0rem',
  marginRight: '0rem',
  marginLeft: '0rem',
  hoverDecorationColor: 'gray70',
  activeDecorationColor: 'gray100',
  hoverTextColor: 'gray70',
  activeTextColor: 'gray100',
  textColorVariant: '',
  type: null,
  hoverType: 'underline',
  pointerEvent: 'auto',
  avoidNextLink: false,
  title: null,
  tabIndex: 0,
};

const getStyleColor = (colors: IDictionary, colorVariant?: string): string => {
  if (colorVariant) return colors[colorVariant];
  return 'var(--gray-100)';
};

/* eslint-disable @typescript-eslint/indent */
const AnchorContainer = styled.a<AnchorContainerProps>`
  width: fit-content;
  margin-top: ${({ marginTop }): string | undefined => marginTop};
  margin-bottom: ${({ marginBottom }): string | undefined => marginBottom};
  margin-right: ${({ marginRight }): string | undefined => marginRight};
  margin-left: ${({ marginLeft }): string | undefined => marginLeft};
  display: ${({ display }): string | undefined => display};
  text-decoration: ${({ textDecoration }): string | undefined =>
    textDecoration};
  text-decoration-color: ${(props): string =>
    props.colorVariant && props.theme.colors[props.colorVariant]};
  border-bottom: ${(props): string =>
    props.type === 'sitemap' ? '0.125rem var(--beek-blue) solid' : ''};
  pointer-events: ${(props): string =>
    props.pointerEvent ? props.pointerEvent : 'auto'};
  &:hover {
    text-decoration: ${(props): string | undefined =>
      props.hoverType === 'sitemap' ? '' : props.hoverTextDecoration};
    text-decoration-color: ${(props): string =>
      props.hoverDecorationColor
        ? getStyleColor(props.theme.colors, props.hoverDecorationColor)
        : 'var(--gray-70);'};
    h1,
    h2,
    h3,
    h4,
    span,
    p {
      color: ${(props): string =>
        props.hoverTextColor
          ? getStyleColor(props.theme.colors, props.hoverTextColor)
          : 'var(--gray-70);'};
    }
  }
  &:active,
  &:visited {
    text-decoration: ${({ activeTextDecoration }): string | undefined =>
      activeTextDecoration};
    text-decoration-color: ${(props): string =>
      props.activeDecorationColor
        ? getStyleColor(props.theme.colors, props.activeDecorationColor)
        : 'var(--gray-100);'};
    h1,
    h2,
    h3,
    h4,
    span,
    p {
      color: ${(props): string =>
        props.activeTextColor
          ? getStyleColor(props.theme.colors, props.activeTextColor)
          : 'var(--gray-100);'};
    }
  }
`;
/* eslint-enable @typescript-eslint/indent */

const Link = (props: LinkProps): JSX.Element => {
  const {
    textColorVariant,
    href,
    children,
    external,
    prefetch,
    typographyType,
    colorVariant,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    display,
    textDecoration,
    type,
    hoverType,
    pointerEvent,
    avoidNextLink,
    title,
    locale,
    tabIndex,
  } = props;
  const titleLink = typeof children === 'string' ? children : title || '';

  if (external) {
    return (
      <AnchorContainer
        marginTop={marginTop}
        marginBottom={marginBottom}
        marginLeft={marginLeft}
        marginRight={marginRight}
        colorVariant={colorVariant}
        textDecoration={textDecoration}
        display={display}
        title={titleLink}
        href={href}
        type={type}
        hoverType={hoverType}
        pointerEvent={pointerEvent}
        target="_blank"
        rel="noreferrer"
      >
        <Typography
          display={display}
          tag="span"
          type={typographyType}
          colorVariant={textColorVariant}
        >
          {children}
        </Typography>
      </AnchorContainer>
    );
  }
  return (
    <NextLink
      prefetch={prefetch}
      href={href}
      avoidNextLink={avoidNextLink}
      locale={locale}
    >
      <AnchorContainer
        type={type}
        hoverType={hoverType}
        pointerEvent={pointerEvent}
        colorVariant={colorVariant}
        marginTop={marginTop}
        marginBottom={marginBottom}
        marginLeft={marginLeft}
        marginRight={marginRight}
        textDecoration={textDecoration}
        display={display}
        title={titleLink}
        href={href}
        tabIndex={tabIndex}
      >
        <Typography
          display={display}
          tag="span"
          type={typographyType}
          colorVariant={textColorVariant}
        >
          {children}
        </Typography>
      </AnchorContainer>
    </NextLink>
  );
};
Link.defaultProps = defaultProps;
export default Link;
